import React, { useState, useEffect } from "react";
import { Modal, Input, Button, message, Alert, Spin } from "antd";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
  deleteWorkspace,
  previewWorkspaceDeletion,
} from "../../api/internal_tool_endpoints";

// How long to wait after the operator stops typing before asking the server
// what this workspace actually holds. The preview scans several collections, so
// firing it per keystroke would be wasteful for no benefit.
const PREVIEW_DEBOUNCE_MS = 500;
// Don't ask about a fragment. Workspace ids are lowercase words joined by
// underscores and none is this short, so a one- or two-character value is
// someone mid-word — and the preview's account count is a full-collection read,
// the one unbounded query on this path.
const PREVIEW_MIN_ID_LENGTH = 3;

const DeleteWorkspaceModal = ({ open, onClose, onSuccess }) => {
  const [idClient, setIdClient] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  // Dry-run equivalent for this path: the operator script previews by default,
  // but here the confirmation executes immediately, so the real volume has to be
  // on screen before they type it.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    const target = idClient.trim();
    if (!open || target.length < PREVIEW_MIN_ID_LENGTH) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    const timer = setTimeout(async () => {
      try {
        const result = await previewWorkspaceDeletion(target);
        if (!cancelled) setPreview(result);
      } catch (e) {
        // A failed preview must never be the reason a delete can't proceed —
        // surface it and leave the static list as the fallback.
        if (!cancelled) {
          setPreview(null);
          setPreviewError(e.message || "Preview unavailable");
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [idClient, open]);

  const handleDelete = async () => {
    const target = idClient.trim();
    if (!target) {
      message.error("Please enter a workspace ID");
      return;
    }

    // Confirm by typing the workspace id itself (GitHub-style) so an operator
    // can't confirm-and-delete the wrong workspace. The typed id is also sent
    // as the server-side confirmation token.
    if (confirmText.trim() !== target) {
      message.error("Type the workspace id exactly to confirm");
      return;
    }

    setLoading(true);

    try {
      const result = await deleteWorkspace({
        id_client: target,
        confirm: target,
      });

      message.success(
        `Deletion started for "${target}". It will disappear from the monitor once the background purge completes.`
      );

      // Reset form
      setIdClient("");
      setConfirmText("");

      // Call success callback
      if (onSuccess) {
        onSuccess(result);
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error deleting workspace:", error);
      message.error(
        error.message || "Failed to delete workspace. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIdClient("");
    setConfirmText("");
    onClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <DeleteOutlined style={{ color: "#ff4d4f" }} />
          <span>Delete Workspace</span>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="delete"
          type="primary"
          danger
          loading={loading}
          onClick={handleDelete}
          disabled={
            !idClient.trim() || confirmText.trim() !== idClient.trim()
          }
        >
          Permanently Delete Workspace
        </Button>,
      ]}
      width={600}
    >
      <div className="flex flex-col gap-4 py-4">
        <Alert
          message="Warning: This action is irreversible!"
          description="This will permanently remove the workspace and all related data. This action cannot be undone."
          type="error"
          icon={<ExclamationCircleOutlined />}
          showIcon
        />

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">
            Workspace ID (id_client) <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter workspace ID (e.g., pepsi_fr, ko_global)"
            value={idClient}
            onChange={(e) => setIdClient(e.target.value)}
            disabled={loading}
            size="large"
          />
          <span className="text-xs text-gray-500">
            Enter the workspace ID (id_client) you want to delete
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">
            Type the workspace id to confirm{" "}
            <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder={
              idClient.trim()
                ? `Type "${idClient.trim()}" to confirm`
                : "Type the workspace id above to confirm"
            }
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={loading}
            size="large"
          />
          <span className="text-xs text-gray-500">
            Re-type the exact workspace id. This guards against deleting the
            wrong workspace and is sent as the server-side confirmation token.
          </span>
        </div>

        {previewLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spin size="small" />
            <span>Checking what this workspace holds...</span>
          </div>
        )}

        {previewError && (
          <Alert
            type="info"
            showIcon
            message="Could not read this workspace's contents"
            description={`${previewError}. The categories below still apply; the actual counts are unavailable.`}
          />
        )}

        {preview && !previewLoading && (
          <Alert
            type={
              preview.users_left_without_workspace > 0 ? "error" : "warning"
            }
            showIcon
            message={`This workspace holds:`}
            description={
              <div className="mt-2 text-sm">
                {Object.entries(preview.counts || {}).filter(
                  ([, n]) => n > 0
                ).length > 0 ? (
                  <ul className="list-disc pl-5">
                    {Object.entries(preview.counts)
                      .filter(([, n]) => n > 0)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([collection, n]) => (
                        <li key={collection}>
                          <code>{collection}</code>: {n}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <div>
                    No workspace-keyed documents found — check the workspace id
                    is correct.
                  </div>
                )}
                <div className="mt-2">
                  <strong>{preview.users_affected}</strong> account
                  {preview.users_affected === 1 ? "" : "s"} lose access to it.
                </div>
                {preview.users_left_without_workspace > 0 && (
                  <div className="mt-1">
                    <strong>
                      {preview.users_left_without_workspace} of those would be
                      left with no workspace at all.
                    </strong>{" "}
                    Their accounts stay enabled and can still sign in with
                    nothing to open — this action does not disable them. Use{" "}
                    <code>remove_workspaces.py --offboard-emptied</code> if they
                    should be offboarded.
                  </div>
                )}
                {preview.clickhouse_applicable === false && (
                  <div className="mt-2">
                    ClickHouse will <strong>not</strong> be purged: staging
                    shares production&apos;s database, so those rows are cleaned
                    by the production delete.
                  </div>
                )}
                {Object.keys(preview.errors || {}).length > 0 && (
                  <div className="mt-2 text-gray-500">
                    Could not read: {Object.keys(preview.errors).join(", ")}.
                  </div>
                )}
              </div>
            }
          />
        )}

        <Alert
          message="What will be deleted:"
          description={
            <ul className="list-disc pl-5 mt-2 text-sm">
              <li>
                Workspace configuration: formula, geoscope (with its per-market
                scope), entitlements and UI config
              </li>
              <li>
                Workspace access removed from all users who had it (Dashmote
                staff + client users), including their saved column layouts for
                it
              </li>
              <li>
                API keys and invite links for this workspace — they stop working
                immediately
              </li>
              <li>
                Saved lists and saved filter views, workflow visibility
                overrides, scheduled canvas emails, public canvas/insight share
                links, and derived caches
              </li>
              <li>
                Export styles and email campaigns shared with other workspaces
                are kept — only this workspace is unassigned from them
              </li>
              <li>
                ClickHouse serving data (DWS/ADS) + guarded own-id DWD rows +
                the dwd_id_client_mapping row
              </li>
              <li className="text-gray-500">
                Content the workspace&apos;s users created — chats, canvases,
                reports, insights — is retained.
              </li>
              <li className="text-gray-500">
                The ClickHouse purge runs as a background job; the row drops
                from the monitor once it completes.
              </li>
            </ul>
          }
          type="warning"
          showIcon
        />
      </div>
    </Modal>
  );
};

export default DeleteWorkspaceModal;
